/**
 * Browser Web Serial helpers for digital weighing scales (USB/COM).
 * Chrome / Edge only — requires a user gesture to pick the port.
 */

export const SCALE_BAUD_RATES = [2400, 4800, 9600, 19200, 38400, 57600, 115200];

export const isWebSerialSupported = () =>
  typeof navigator !== "undefined" && !!navigator.serial;

/** Extract kg from common scale text lines. */
export function parseScaleWeight(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return null;

  const stable = !/\b(US|UNSTABLE|MOT|DYNAMIC)\b/i.test(text)
    && (/\b(ST|STABLE)\b/i.test(text) || !/\bUS\b/i.test(text));

  // Prefer explicit kg / g markers
  let m = text.match(/([+-]?\d+(?:[.,]\d+)?)\s*(kg|kgs|kilogram)/i);
  if (m) {
    return {
      kg: toNumber(m[1]),
      unit: "kg",
      stable,
      raw: text,
    };
  }

  m = text.match(/([+-]?\d+(?:[.,]\d+)?)\s*g\b/i);
  if (m) {
    return {
      kg: toNumber(m[1]) / 1000,
      unit: "g",
      stable,
      raw: text,
    };
  }

  // Generic: last number on the line (many Chinese scales)
  m = text.match(/([+-]?\d+(?:[.,]\d+)?)\s*$/);
  if (m) {
    const n = toNumber(m[1]);
    // Heuristic: values > 500 with no unit are often grams
    const kg = n > 500 ? n / 1000 : n;
    return { kg, unit: n > 500 ? "g?" : "kg?", stable, raw: text };
  }

  return null;
}

function toNumber(s) {
  const n = Number.parseFloat(String(s).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Open a serial port and stream line-delimited weight readings.
 * @returns {{ port, close, getInfo }}
 */
export async function connectScaleSerial({
  baudRate = 9600,
  onLine,
  onError,
  onDisconnect,
} = {}) {
  if (!isWebSerialSupported()) {
    throw new Error("Web Serial is not supported in this browser. Use Chrome or Edge on desktop.");
  }

  const port = await navigator.serial.requestPort();
  await port.open({ baudRate: Number(baudRate) || 9600, dataBits: 8, stopBits: 1, parity: "none" });

  const decoder = new TextDecoderStream();
  const readableClosed = port.readable.pipeTo(decoder.writable).catch((err) => {
    if (err?.name !== "AbortError") onError?.(err);
  });
  const reader = decoder.readable.getReader();

  let buffer = "";
  let closed = false;

  const pump = async () => {
    try {
      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += value;
        const parts = buffer.split(/\r\n|\n|\r/);
        buffer = parts.pop() ?? "";
        parts.forEach((line) => {
          const trimmed = line.trim();
          if (trimmed) onLine?.(trimmed);
        });
      }
    } catch (err) {
      if (!closed) onError?.(err);
    } finally {
      try { reader.releaseLock(); } catch { /* ignore */ }
      onDisconnect?.();
    }
  };

  pump();

  return {
    port,
    async close() {
      closed = true;
      try { await reader.cancel(); } catch { /* ignore */ }
      try { await readableClosed; } catch { /* ignore */ }
      try {
        if (port.readable) await port.readable.cancel();
      } catch { /* ignore */ }
      try { await port.close(); } catch { /* ignore */ }
    },
    getInfo() {
      try {
        return port.getInfo?.() || {};
      } catch {
        return {};
      }
    },
  };
}

/** Demo simulator — emits fake scale lines without hardware. */
export function startScaleSimulator({ onLine, intervalMs = 400 } = {}) {
  let t = 0;
  let base = 12.4;
  const id = setInterval(() => {
    t += 1;
    // Settle after a few ticks
    const jitter = t < 8 ? (Math.random() - 0.5) * 0.35 : (Math.random() - 0.5) * 0.02;
    if (t % 40 === 0) base = 5 + Math.random() * 40;
    const kg = Math.max(0, base + jitter);
    const stable = t >= 8 && Math.abs(jitter) < 0.05;
    const line = `${stable ? "ST" : "US"},GS,+${kg.toFixed(3)}kg`;
    onLine?.(line);
  }, intervalMs);

  return {
    stop() {
      clearInterval(id);
    },
  };
}
